import { rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import { Resource } from '__types__';
import React from 'react';
import { Trans } from 'react-i18next';
import { CollectionField } from 'v5/patterns/widgets/CollectionField';
import store from '../../../../../store';
import { openContactSidebar } from '../../../../../store/v1/ui/ui.actions';
import { myServices } from '../../../../../v5/services/services.connected.app';
import {
  resourceQueryUtils,
  ResourceSearchQuery
} from '../../../../../v5/api/resource/resource.api';
import { rowLevelValidation } from '../../../../../v5/platform/row-level-validation';
import { renderName } from '../../../../helpers/contactHelpers';
import { ResourceMeta, UserCircleImage } from '../../byModel';
import { resourceDisplayName } from 'v5/patterns/resource/resourceDisplayName';

export function ResourceComboboxTriggerItem({
  resource
}: {
  resource: Resource;
}) {
  const { id, profile_picture, first_name, last_name, groups } = resource;
  return (
    <div className="flex flex-row gap-2 items-center">
      <UserCircleImage
        src={profile_picture}
        initials={{ first_name, last_name }}
        contact_id={id}
        // @ts-expect-error migrate to ts
        colours={groups && groups.map(group => group.colour)}
        className="size-10 rounded-sm!"
      />
      <div>{renderName(resource)}</div>
      <span
        onClick={() => {
          store.dispatch(openContactSidebar(resource));
        }}
        className="invisible group-hocus/pill:visible"
      >
        <img
          className="size-4"
          src="/images/icon_contact_sidebar_hint.svg"
          alt=""
        />
      </span>
    </div>
  );
}

export function ResourceMenuItemContent(props: {
  resource: Resource;
  hideRole?: boolean;
}) {
  const { resource } = props;
  return (
    <>
      <UserCircleImage
        src={resource.profile_picture}
        initials={resource}
        contact_id={resource.id}
        // @ts-expect-error migrate to ts
        colours={resource.groups && resource.groups.map(group => group.colour)}
        className="size-10 rounded-sm!"
      />
      <div className="mr-auto">{renderName(resource)}</div>
      <ResourceMeta hideRole={props.hideRole} resource={resource} />
    </>
  );
}

export const ResourceSelect = injectConfig(CollectionField<Resource>)
  .fromProps<{ query: ResourceSearchQuery }>(api => ({
    // promise-like function to resolve id to the item
    getTagProps: () => ({ radius: 'sm', className: 'w-full' }),
    useResolveById: () => id =>
      myServices.resourceService.ensureItem(Number(id)),
    useOptions: (query, isDropdownOpen) => {
      const props = api.useProps(); // todo resource service
      const data = myServices.resourceService.useList(
        resourceQueryUtils
          .transform(props.query)
          .build(q => [q.withSearch(query)])
      );

      return rd.useMemoMap(data, data =>
        rowLevelValidation.validDataFromRows(data.items).map(resource => ({
          id: String(resource.id),
          label: resourceDisplayName(resource),
          content: <ResourceMenuItemContent resource={resource} />
        }))
      );
    },
    promptRenderer: data => (
      <Trans
        ns="v1_shared"
        i18nKey="TagInput.add"
        context={data.length ? 'many' : undefined}
      />
    ),
    getId: x => String(x.id),
    rowRenderer: resource => (
      <ResourceComboboxTriggerItem resource={resource} />
    ),
    useGetGhostItem: () => () => {
      throw new Error('Not implemented');
    },
    useCreatePromise: () => query => Promise.reject(),
    useSelectedItems: keys =>
      myServices.resourceService.useItemsById(keys.map(Number))
  }))
  .transformProps(x => x.passAll);
